// Gate B3, targeting (decisions 0043, 0059): the spike's side of @rpg/combat's chooser and lock-on.
//   - soft auto-targeting is the default: `soft()` is what combat asks when a move starts (the combat stream's seam);
//   - with the lock-on setting on (Settings → Controls → Targeting), the lock button (the `lock` action, in the
//     combat context) locks the best target by the lock weights, frames hero and target with the camera's lock-on
//     profile, and switches targets by a flick of the look input: the mouse, the pad's right stick and a swipe on the
//     touch look area are all one `look` action, so one FlickDetector serves the three; a lock drops on the target's
//     death or past the drop range;
//   - the touch overlay's lock button is shown while the combat context is active and the setting is on
//     (`mountLockButton`, the input notes: "combat reveals the lock button").
//
// Clocks: the lock is sim state (it decides where attacks go), stepped in the sim phase from the lock press and a
// flick sampled onto the input tape (`actions.sample`), so a replay locks and switches exactly as recorded. The flick
// is detected on the view side, where look input arrives (on real time, decision 0011): while locked, look input
// turns into flicks instead of orbiting the camera, which the lock-on profile turns to face the target.
import { chooseTarget, FlickDetector, LockOn, type LockChange, type TargetCandidate, type TargetFrom, type TargetPoint, type TargetWeights } from "@rpg/combat";
import type { Actions, Runtime, Vec2 } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { TargetingRow } from "../content/types.gen";
import type { LookSource, SpikeCamera } from "../camera";
import type { SpikeInput } from "../input";

/** The setting that turns lock-on on, and its values. */
export const TARGETING_SETTING = "targeting.mode";
export type TargetingSettingValue = "soft" | "lockOn";

/** The targeting table's row as the engine's types. */
export function targetingValues(content: SpikeContent): TargetingRow {
  const row = content.registry.get("targeting", "default") as TargetingRow | undefined;
  if (!row) throw new Error("targeting: the targeting table has no 'default' row");
  return row;
}

/** What the targeting needs from its scene. */
export interface TargetingDeps {
  runtime: Runtime;
  actions: Actions;
  content: SpikeContent;
  /** A setting's value now (`targeting.mode`). */
  setting: (id: string) => unknown;
  /** The hero now: where it stands, which way it faces and, while the player steers, the stick as a world
   *  direction `[x, z]`. Read in the sim step. */
  from: () => TargetFrom;
  /** Everyone who may be targeted now. */
  candidates: () => readonly TargetCandidate[];
  /** Line of sight between two eye points (the scene's collision query). */
  lineOfSight: (from: TargetPoint, to: TargetPoint) => boolean;
  /** The camera to frame with (null: none). */
  camera: SpikeCamera | null;
}

/** The mounted targeting. */
export interface Targeting {
  /** "soft", or "lock" when the setting allows lock-on. */
  mode(): "soft" | "lock";
  /** The locked target, or null. */
  locked(): number | null;
  /** What an attack starting now turns to: the locked target, else the soft choice (combat's seam). */
  current(): number | null;
  /** The soft chooser's pick now. */
  soft(from?: TargetFrom): number | null;
  /** Wrap a look source: while locked, look input becomes flicks and doesn't orbit the camera. */
  look(source: LookSource): LookSource;
  /** Lock changes so far. */
  changes(): readonly LockChange[];
  /** Flicks seen on the view side (and taken by the sim), by direction. */
  flicks(): { seen: number; taken: number };
  state(): { mode: "soft" | "lock"; locked: number | null; profile: string | null };
  /** Stop. */
  unmount(): void;
}

/** Mount targeting on a scene. */
export function mountTargeting(d: TargetingDeps): Targeting {
  const row = targetingValues(d.content);
  const lock = new LockOn({ weights: row.lock as TargetWeights, dropRange: row.dropRange });
  const flick = new FlickDetector(row.flick);
  const eye = row.eyeHeight;
  const los = (a: TargetPoint, b: TargetPoint): boolean => d.lineOfSight([a[0], a[1] + eye, a[2]], [b[0], b[1] + eye, b[2]]);
  let pending = 0;
  let seen = 0;
  let taken = 0;
  const modeNow = (): "soft" | "lock" => (d.setting(TARGETING_SETTING) === "lockOn" ? "lock" : "soft");

  const offs = [
    d.runtime.on(
      "sim",
      () => {
        // sampled onto the tape: the setting and the flick decide the sim, so a replay reads them as recorded
        const mode = d.actions.sample("targeting.mode", () => (modeNow() === "lock" ? 1 : 0)) === 1 ? "lock" : "soft";
        const flicked = d.actions.sample("targeting.flick", () => {
          const f = pending;
          pending = 0;
          return f;
        });
        const pressed = d.actions.pressed("lock");
        const from = d.from();
        const cands = d.candidates();
        if (mode === "soft") {
          lock.release("released");
          return;
        }
        if (pressed) {
          lock.toggle(from, cands, { lineOfSight: los });
          flick.reset();
        }
        if (flicked !== 0 && lock.target !== null) {
          taken++;
          lock.switchBy(flicked, { yaw: d.camera?.rig.yaw ?? from.facing }, from, cands, { lineOfSight: los });
        }
        lock.update(from, cands);
      },
      { name: "targeting: lock", order: 10 },
    ),
  ];

  // the camera: the lock-on profile while locked (on the context layer it shares with combat; the request it
  // replaced comes back on release), and the target as the rig's focus
  let framing = false;
  let before: string | undefined;
  if (d.camera) {
    const cam = d.camera;
    offs.push(
      d.runtime.on(
        "late",
        () => {
          const id = lock.target;
          const t = id === null ? undefined : d.candidates().find((c) => c.id === id);
          if (t) {
            if (!framing) {
              before = cam.rig.profiles.snapshot()["context"];
              cam.request("lock-on");
              framing = true;
            }
            cam.rig.setFocus({ x: t.pos[0], y: t.pos[1], z: t.pos[2] });
          } else if (framing) {
            framing = false;
            cam.rig.setFocus(null);
            if (before) cam.request(before);
            else cam.release("context");
          }
        },
        { order: -20, name: "targeting: framing" },
      ),
    );
  }

  return {
    mode: modeNow,
    locked: () => lock.target,
    current: () => lock.target ?? chooseTarget(d.from(), d.candidates(), row.soft as TargetWeights, { lineOfSight: los }),
    soft: (from = d.from()) => chooseTarget(from, d.candidates(), row.soft as TargetWeights, { lineOfSight: los }),
    look: (source) => (dt) => {
      const v: Vec2 = source(dt);
      if (lock.target === null) return v;
      const f = flick.push(v.x, dt);
      if (f !== 0) {
        pending = f;
        seen++;
      }
      return { x: 0, y: 0 };
    },
    changes: () => lock.changes,
    flicks: () => ({ seen, taken }),
    state: () => ({ mode: modeNow(), locked: lock.target, profile: framing ? "lock-on" : null }),
    unmount: () => offs.forEach((o) => o()),
  };
}

/**
 * The touch overlay's lock button: shown while the combat context is active and lock-on is the setting, hidden
 * otherwise (`?touch=all` shows every button and is left alone). Mounted once for the whole game.
 */
export function mountLockButton(o: { runtime: Runtime; input: SpikeInput; setting: (id: string) => unknown; search?: string }): () => void {
  if (new URLSearchParams(o.search ?? location.search).get("touch") === "all") return () => undefined;
  let shown: boolean | null = null;
  return o.runtime.on(
    "input",
    () => {
      const want = o.input.input.contexts.active().includes("combat") && o.setting(TARGETING_SETTING) === "lockOn";
      if (want === shown) return;
      shown = want;
      o.input.overlay.reveal("lock", want);
    },
    { name: "targeting: lock button" },
  );
}
